<template>
  <div class="content-image-choice" :key="render" :class="{ 'preview-mode': preview }">
    <!-- Title Images Section -->
    <div class="section-block title-section" v-if="!preview">
      <div class="section-header">
        <div class="section-title">
          <div class="title-with-toggle">
            <h3>{{ $t('survey.title_images') }}</h3>
            <label class="toggle-switch">
              <input
                type="checkbox"
                v-model="question.showImageOnTitle"
                @change="$store.commit('survey/upsertQuestion', question)">
              <span class="toggle-slider"></span>
            </label>
          </div>
          <div class="view-mode" v-if="question.showImageOnTitle">
            <div class="control-group">
              <label class="control-label">{{ $t('survey.grid_layout') }}</label>
              <div class="size-options">
                <button class="size-toggle" @click="showTitleSizeMenu = !showTitleSizeMenu">
                  <i class="material-symbols-outlined">{{ titleSizes.find(s => s.value === currentTitleSize)?.icon }}</i>
                  <span>{{ $t(`survey.columns.${titleSizes.find(s => s.value === currentTitleSize)?.value}`) }}</span>
                  <i class="material-symbols-outlined">expand_more</i>
                </button>
                <div class="size-menu" v-if="showTitleSizeMenu" v-click-outside.click="() => showTitleSizeMenu = false">
                  <button v-for="size in titleSizes"
                          :key="size.value"
                          class="size-option"
                          :class="{ active: currentTitleSize === size.value }"
                          @click="() => { currentTitleSize = size.value; showTitleSizeMenu = false }"
                          v-title="$t(`survey.view_descriptions.${size.value}`)" title-placement="left">
                    <i class="material-symbols-outlined">{{ size.icon }}</i>
                    <span>{{ $t(`survey.columns.${size.value}`) }}</span>
                  </button>
                </div>
              </div>
            </div>
            <div class="control-group">
              <label class="control-label">{{ $t('survey.hide_labels') }}</label>
              <div class="label-toggle">
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    :checked="question.hideTitleLabels"
                    @change="toggleAllTitleLabels">
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="section-content" v-if="question.showImageOnTitle">
        <TitleImages :question="question" />
      </div>
    </div>

    <!-- Subject Options Section -->
    <div class="section-block subjects-section" v-if="!preview">
      <div class="section-header">
        <div class="section-title">
          <div><h3>{{ $t('survey.subject_options') }}</h3></div>
          <div class="view-mode">
            <div class="control-group">
              <label class="control-label">{{ $t('survey.grid_layout') }}</label>
              <div class="size-options">
                <button class="size-toggle" @click="showOptionSizeMenu = !showOptionSizeMenu">
                  <i class="material-symbols-outlined">{{ optionSizes.find(s => s.value === currentOptionSize)?.icon }}</i>
                  <span>{{ $t(`survey.columns.${optionSizes.find(s => s.value === currentOptionSize)?.value}`) }}</span>
                  <i class="material-symbols-outlined">expand_more</i>
                </button>
                <div class="size-menu" v-if="showOptionSizeMenu" v-click-outside.click="() => showOptionSizeMenu = false">
                  <button v-for="size in optionSizes"
                          :key="size.value"
                          class="size-option"
                          :class="{ active: currentOptionSize === size.value }"
                          @click="() => { currentOptionSize = size.value; showOptionSizeMenu = false }"
                          v-title="$t(`survey.view_descriptions.${size.value}`)" title-placement="left">
                    <i class="material-symbols-outlined">{{ size.icon }}</i>
                    <span>{{ $t(`survey.columns.${size.value}`) }}</span>
                  </button>
                </div>
              </div>
            </div>
            <div class="control-group">
              <label class="control-label">{{ $t('survey.hide_labels') }}</label>
              <div class="label-toggle">
                <label class="toggle-switch">
                  <input
                    type="checkbox"
                    :checked="question.hideAllSubjectLabels"
                    @change="toggleAllSubjectLabels">
                  <span class="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="section-content">
        <div class="subjects-content">
          <div v-for="(subject, subjectIndex) in question.subjects"
               v-bind:key="subjectIndex"
               :id="'subject-' + subjectIndex"
               class="subject-container" :style="{'background-color': survey.colors.splashBg}">
            <!-- Regular edit mode with all features -->
            <button class="clean-delete-button" v-if="canRemoveSubject" @click="deleteSubject(subjectIndex)" v-title="$t('survey.delete_subject')">
              <i class="material-icons">close</i>
            </button>

            <div class="subject-header">
              <div class="subject-label">
                <app-form-textarea-limited
                  :inputMaxHeight="500"
                  :inputMinHeight="20"
                  :identify="subjectIndex"
                  v-model="subject.label"
                  class="description-area"
                  :colorText="colors.splashColor"
                  name="image-choice-description"
                  @updateSurveyTitle="updateQuestionDescription($event, subjectIndex)"
                  v-title="$t('survey.edit_subject_label')"/>
              </div>

              <div class="subject-limits">
                <div class="limit-group">
                  <div class="limit-input">
                    <label>{{ $t('survey.minimum') }}</label>
                    <input
                      type="number"
                      v-model.number="subject.min"
                      :min="1"
                      :max="subject.max || subject.options.length"
                      class="limit-value-input"
                      :class="{ invalid: hasMinErrors[subjectIndex] }"
                      @input="handleLimitInput($event, subjectIndex, 'min')"
                      @blur="validateLimits(subjectIndex)"
                      :key="`min-${subjectIndex}`"
                    />
                  </div>
                  <div class="limit-input">
                    <label>{{ $t('survey.maximum') }}</label>
                    <input
                      type="number"
                      v-model.number="subject.max"
                      :min="subject.min || 1"
                      :max="subject.options.length"
                      class="limit-value-input"
                      :class="{ invalid: hasMaxErrors[subjectIndex] }"
                      @input="handleLimitInput($event, subjectIndex, 'max')"
                      @blur="validateLimits(subjectIndex)"
                      :key="`max-${subjectIndex}`"
                    />
                  </div>
                </div>
                <div class="limit-hint">
                  <!-- Select between {{ subject.min || 1 }} and {{ subject.max || subject.options.length }} images -->
                   {{ $t('survey.select_range', { min: subject.min || 1, max: subject.max || subject.options.length }) }}
                </div>
                <div class="limit-error" v-if="limitErrors[subjectIndex]">
                  {{ limitErrors[subjectIndex] }}
                </div>
              </div>
            </div>

            <!-- Regular draggable options -->
            <div class="subject-options-container">
              <div class="subject-options">
                <draggable
                  v-model="subject.options"
                  :animation="200"
                  :disabled="false"
                  ghost-class="ghost-card"
                  drag-class="dragging"
                  group="images"
                  @change="optionOrderChanged(subjectIndex)"
                  @start="onDragStart"
                  @end="onDragEnd"
                  class="draggable-container">
                  <template>
                    <transition-group name="option-list" tag="div" class="option-list-container">
                      <div v-for="(option, optionIndex) in subject.options"
                        class="subject-image-choice"
                        :class="{ 'is-dragging': isDragging }"
                        :key="'-' + subjectIndex + '-' + optionIndex"
                        :data-size="currentOptionSize">
                        <div class="image-container">
                          <div class="image-wrapper">
                            <button
                              v-if="canRemoveOption(subjectIndex)"
                              class="delete-button"
                              @click="removeOption(subjectIndex, optionIndex)"
                              v-title="$t('survey.delete_image')">
                              <i class="material-symbols-outlined">close</i>
                            </button>
                            <ImageUploaderV2 :id="`uploader-${subject.label}-${optionIndex}`"
                              :title="$t('sendhub.set_image_text')"
                              :imageProps="imagePropsDefault(question, optionIndex, subjectIndex, option)"
                              context="questions"
                              :menuOptions='[]'
                              @resetAutoStartUpload="option.autoStartUpload = false"
                              @submenu-action="handleImageAction($event, subjectIndex, optionIndex)"
                              :disableMenus="isDragging" />
                          </div>
                        </div>
                        <div class="option-value" v-if="!question.hideAllSubjectLabels">
                          <div class="input-wrapper">
                            <textarea
                              v-model="option.value"
                              class="option-value-input"
                              :class="{
                                'has-error': option.error,
                                'is-focused': focusedIndex === optionIndex
                              }"
                              :placeholder="$t('survey.option_value_placeholder')"
                              :data-index="optionIndex"
                              maxlength="50"
                              @input="handleOptionInput($event, optionIndex, subjectIndex)"
                              @focus="handleFocus($event, optionIndex)"
                              @blur="handleBlur($event, optionIndex, subjectIndex)"
                              @click="handleTextareaClick($event, optionIndex, subjectIndex)"
                              @keydown="preventInvalidInput"
                              rows="1"
                              required
                            ></textarea>
                            <div class="character-counter"
                                 :class="{
                                   'near-limit': option.value && option.value.length >= 40,
                                   'at-limit': option.value && option.value.length >= 50
                                 }">
                              <span>{{ option.value ? option.value.length : 0 }}/50</span>
                            </div>
                          </div>
                          <div v-if="option.error" class="option-error">{{ option.error }}</div>
                        </div>
                      </div>
                      <div class="subject-image-choice add-image-button no-drag"
                           :data-size="currentOptionSize"
                           @click="addOption(subjectIndex)"
                           v-title="canAddImage(subjectIndex) ? $t('survey.add_new_option') : 'Maximum of 10 images allowed per subject'"
                           :class="{ 'disabled': !canAddImage(subjectIndex) }"
                           :key="'add-button-' + subjectIndex">
                        <div class="option-wrapper add-wrapper">
                          <div class="add-content">
                            <i class="material-symbols-outlined">add_circle</i>
                            <span>{{ $t('survey.add_image') }}</span>
                          </div>
                        </div>
                      </div>
                    </transition-group>
                  </template>
                </draggable>
              </div>
            </div>
          </div>
        </div>

        <div class="add-item">
          <button @click="addItem()" v-title="$t('survey.add_new_subject')">
            <i class="material-symbols-outlined add">format_list_bulleted_add</i>
          </button>
        </div>
      </div>
    </div>

    <!-- Simple preview mode -->
    <div v-if="preview" class="preview-container">
      <div class="preview-samples">
        <!-- Title images preview -->
        <div v-if="question.showImageOnTitle && question.imageOnTitle?.length" class="preview-title-images">
          <div class="preview-thumbs">
            <div
              v-for="index in Math.min(2, titleImagesCount)"
              :key="'title-'+index"
              class="preview-thumb-box">
            </div>
            <div v-if="titleImagesCount > 2" class="preview-more">+{{ titleImagesCount - 2 }}</div>
          </div>
          <div class="preview-count">{{ titleImagesCount }} {{ $tc('survey.preview.title_images', titleImagesCount) }}</div>
        </div>

        <div v-if="question.showImageOnTitle && question.imageOnTitle?.length" class="preview-divider"></div>

        <!-- Subject options preview -->
        <div class="preview-subject-options">
          <div class="preview-thumbs">
            <div
              v-for="index in Math.min(2, totalImages)"
              :key="'subject-'+index"
              class="preview-thumb-box">
            </div>
            <div v-if="totalImages > 2" class="preview-more">+{{ totalImages - 2 }}</div>
          </div>
          <div class="preview-count">{{ getImageCountText }}</div>
        </div>
      </div>
    </div>

    <theme-style>
      .add-item > button, .add-option-button, .delete-subject-button {
        background-color: {{ user.account.theme.colors.accent || '#ff607d' }} !important;
        color: {{ (user.account.theme.colors.primaryText === 'dark') ? '#302b3c' : '#fdfbff' }};
      }
      .add-item > button > .add, .add-option-button i, .add-option-button span, .delete-subject-button i {
        color: {{ (user.account.theme.colors.primaryText === 'dark') ? '#302b3c' : '#fdfbff' }};
      }
      .clean-delete-button i {
        color: {{ user.account.theme.colors.accent || '#ff607d' }};
      }
      .delete-option i {
        color: {{ user.account.theme.colors.accent || '#ff607d' }};
      }
      .delete-option.disabled i {
        color: #999 !important;
      }
      .delete-overlay {
        background-color: {{ user.account.theme.colors.accent || '#ff607d' }}4D;
      }
    </theme-style>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ImageUploaderV2 from '../../image/ImageUploaderV2.vue'
import draggable from 'vuedraggable'
import { eventBus } from '@/_helpers/eventBus.js'
import TitleImages from './TitleImages.vue'
import vClickOutside from 'v-click-outside'

export default {
  name: 'imagechoice',
  props: ['colors', 'preview', 'question'],
  directives: {
    clickOutside: vClickOutside.directive
  },
  components: {
    'app-form-textarea-limited': () => import('@binds-tech/binds-design-system/src/components/Form/TextareaLimited'),
    ImageUploaderV2,
    draggable,
    TitleImages
  },
  data () {
    return {
      inputValue: null,
      render: 0,
      isDragging: false,
      debounceTimer: null,
      titleSizes: [
        {
          value: 'medium',
          label: '2 columns',
          icon: 'grid_view',
          description: 'Default view with 2 columns'
        },
        {
          value: 'large',
          label: '1 column',
          icon: 'view_agenda',
          description: 'Large view with 1 columns'
        }
      ],
      optionSizes: [
        {
          value: 'small',
          label: '4 columns',
          icon: 'view_module',
          description: 'Compact view with 4 columns'
        },
        {
          value: 'medium',
          label: '2 columns',
          icon: 'grid_view',
          description: 'Default view with 2 columns'
        },
        {
          value: 'large',
          label: '1 column',
          icon: 'view_agenda',
          description: 'Large view with 1 columns'
        }
      ],
      limitErrors: {},
      hasMinErrors: {},
      hasMaxErrors: {},
      showTitleSizeMenu: false,
      showOptionSizeMenu: false,
      focusedIndex: null
    }
  },
  computed: {
    survey () {
      const survey = this.$store.getters['survey/getSurvey']
      return survey
    },
    ...mapGetters({
      user: 'account/getUser'
    }),

    /**
     * Count total images across all subjects
     */
    totalImages () {
      if (!this.question.subjects) return 0

      return this.question.subjects.reduce((total, subject) => {
        return total + (subject.options ? subject.options.length : 0)
      }, 0)
    },

    /**
     * Get text description of images
     */
    getImageCountText () {
      const subjects = this.question.subjects ? this.question.subjects.length : 0
      const options = this.totalImages

      if (subjects === 1) {
        return `${options} ${this.$tc('survey.preview.image_choices', options)}`
      }
      return this.$t('survey.preview.subjects_with_total', { subjects, total: options })
    },
    currentTitleSize: {
      get () {
        return this.question.titleImageSize || 'medium'
      },
      set (value) {
        this.$set(this.question, 'titleImageSize', value)
      }
    },
    currentOptionSize: {
      get () {
        return this.question.optionImageSize || 'medium'
      },
      set (value) {
        this.$set(this.question, 'optionImageSize', value)
      }
    },
    titleImagesCount () {
      return this.question.imageOnTitle?.length || 0
    }
  },
  methods: {
    onDragStart () {
      this.isDragging = true
      eventBus.$emit('dragging-started')
    },
    onDragEnd () {
      this.isDragging = false
      eventBus.$emit('dragging-ended')

      // Force a re-render to ensure everything is displayed correctly
      this.$nextTick(() => {
        this.render += 1
      })
    },
    defaultImageOptions () {
      return [
        {
          label: 'https://img.binds.co/437e8dd8-82df-4a39-a30c-43ee8f8a6eea/-/crop/255x237/18,18/-/preview/',
          isHidden: false,
          value: 'imagem 1',
          imageSize: ''
        }
      ]
    },
    getMenuOptions (subjectIndex, optionIndex) {
      const hasImage = !!this.question.subjects[subjectIndex]?.options[optionIndex]?.label
      const canDelete = this.canRemoveOption(subjectIndex)
      const option = this.question.subjects[subjectIndex].options[optionIndex]

      const result = []

      // Always show delete option but with visual feedback if it can't be deleted
      result.push({
        label: canDelete ? 'Remover' : 'Não é possível remover',
        icon: 'delete',
        action: 'delete-option',
        disabled: !canDelete
      })

      if (hasImage) {
        result.push(
          { label: 'Change Image', icon: 'image_search', action: 'insert-image' },
          {
            label: option.isHidden ? 'Show value' : 'Hide value',
            icon: option.isHidden ? 'visibility' : 'visibility_off',
            action: 'toggle-visibility'
          }
        )
      } else {
        result.push({ label: 'Insert Image', icon: 'add_photo_alternate', action: 'insert-image' })
      }

      return result
    },

    handleImageAction (action, subjectIndex, optionIndex) {
      if (action === 'delete-option') {
        this.removeOption(subjectIndex, optionIndex)
      } else if (action === 'toggle-visibility') {
        const option = this.question.subjects[subjectIndex].options[optionIndex]
        this.$set(option, 'isHidden', !option.isHidden)
        this.$store.commit('survey/upsertQuestion', { ...this.question })
      }
    },
    imagePropsDefault (question, optionIndex, subjectIndex, item) {
      return {
        id: question.id,
        success: item && item.label,
        where: 'optionImageChoice',
        label: item?.label || '',
        autoStartUpload: item?.autoStartUpload,
        optionIndex,
        subjectIndex
      }
    },
    deleteItem (idx) {
      this.render += 1
      this.question.subjects.splice(idx, 1)
      setTimeout(function () { this.render = true }.bind(this), 200)
      this.$store.commit('survey/upsertQuestion', this.question)
    },
    updateQuestionDescription (_e, _idx) {
      this.$store.commit('survey/upsertQuestion', this.question)
    },
    addItem () {
      this.render += 1
      const lastSubject = this.question.subjects[this.question.subjects.length - 1]
      const newSubject = {
        label: this.$i18n.t('survey.option_added') + (this.question.subjects.length + 1),
        options: JSON.parse(JSON.stringify(lastSubject.options)),
        min: lastSubject.min,
        max: lastSubject.max
      }
      this.question.subjects.push(newSubject)
      this.$store.commit('survey/upsertQuestion', this.question)
      setTimeout(function () { this.render = true }.bind(this), 200)
    },
    addOption (subjectIndex) {
      // Check if we've reached the limit of 10 images
      if (this.question.subjects[subjectIndex].options.length >= 10) {
        this.$toast.error('Maximum of 10 images allowed per subject')
        return
      }

      // Get the highest option value in the subject
      const currentOptions = this.question.subjects[subjectIndex].options

      // Create a new option with value one higher than the current highest
      const newOption = {
        value: `imagem ${currentOptions.length + 1}`,
        label: 'https://img.binds.co/437e8dd8-82df-4a39-a30c-43ee8f8a6eea/-/crop/255x237/18,18/-/preview/',
        autoStartUpload: true
      }

      // Add the new option to the subject without modifying existing options
      this.question.subjects[subjectIndex].options.push(newOption)

      // Update the question in the store
      this.$store.commit('survey/upsertQuestion', this.question)
      this.validateLimitsWithOptions(subjectIndex)

      // Force a re-render
      this.render += 1

      // Scroll to the add button after the DOM has updated
      this.$nextTick(() => {
        this.scrollToAddButton(subjectIndex)
      })
    },
    scrollToAddButton (subjectIndex) {
      // Find the add button within the subject container
      const addButton = document.querySelector(`#subject-${subjectIndex} .add-image-button`)
      if (addButton) {
        const buttonRect = addButton.getBoundingClientRect()
        const offset = window.pageYOffset + buttonRect.top - (window.innerHeight / 2) + (buttonRect.height / 2)
        window.scrollTo({
          top: Math.max(0, offset),
          behavior: 'smooth'
        })
      }
    },
    canRemoveOption (subjectIndex) {
      return this.question.subjects[subjectIndex].options.length > 2
    },
    removeOption (subjectIndex, optionIndex) {
      if (!this.canRemoveOption(subjectIndex)) return

      this.question.subjects[subjectIndex].options.splice(optionIndex, 1)
      this.$store.commit('survey/upsertQuestion', this.question)
      this.validateLimitsWithOptions(subjectIndex)
      this.render += 1
    },
    canRemoveSubject () {
      return this.question.subjects.length > 1
    },
    deleteSubject (subjectIndex) {
      if (!this.canRemoveSubject()) return

      this.question.subjects.splice(subjectIndex, 1)
      this.$store.commit('survey/upsertQuestion', this.question)
      this.render += 1
    },
    optionOrderChanged (subjectIndex) {
      this.$store.commit('survey/upsertQuestion', this.question)
      this.validateLimitsWithOptions(subjectIndex)
      this.render += 1
    },
    // Helper method to get the correct image source for preview mode
    getImageSource (option, subjectIndex, optionIndex) {
      // Check all possible image source paths
      if (option.image && option.image.url) {
        return option.image.url
      } else if (option.imageUrl) {
        return option.imageUrl
      } else if (option.label && option.label !== 'undefined' && !option.label.includes('undefined')) {
        return option.label
      } else if (this.question.subjects &&
                this.question.subjects[subjectIndex] &&
                this.question.subjects[subjectIndex].options[optionIndex] &&
                this.question.subjects[subjectIndex].options[optionIndex].label) {
        return this.question.subjects[subjectIndex].options[optionIndex].label
      }
      // Return null if no valid image source is found
      return null
    },
    handleOptionInput (event, optionIndex, subjectIndex) {
      const textarea = event.target
      let value = textarea.value

      // Clean the input value - allow single spaces but prevent consecutive ones
      value = value
        .replace(/\r?\n|\r/g, '') // Remove newlines
        .replace(/\s{2,}/g, ' ') // Replace multiple spaces with single space
        .replace(/,/g, '') // Remove commas
        .slice(0, 50) // Limit length

      // Check for duplicates in the same subject
      const isDuplicate = this.question.subjects[subjectIndex].options.some(
        (option, index) => index !== optionIndex && option.value === value
      )

      if (isDuplicate) {
        // Remove the last character
        value = value.slice(0, -1)

        // Show error message
        this.$set(this.question.subjects[subjectIndex].options[optionIndex], 'error', this.$t('survey.duplicate_value'))

        // Optional: Clear error after a delay
        setTimeout(() => {
          if (this.question.subjects[subjectIndex].options[optionIndex].error === this.$t('survey.duplicate_value')) {
            this.$set(this.question.subjects[subjectIndex].options[optionIndex], 'error', null)
          }
        }, 2000)
      }

      // Update the value
      this.$set(this.question.subjects[subjectIndex].options[optionIndex], 'value', value)

      // Show required field error if empty
      if (!value.trim()) {
        this.$set(this.question.subjects[subjectIndex].options[optionIndex], 'error', this.$t('survey.required_field'))
      } else if (!isDuplicate) {
        this.$set(this.question.subjects[subjectIndex].options[optionIndex], 'error', null)
      }

      // Adjust height based on content when focused
      if (this.focusedIndex === optionIndex) {
        requestAnimationFrame(() => {
          textarea.style.height = '32px'
          const scrollHeight = Math.min(textarea.scrollHeight, 100)
          textarea.style.height = `${scrollHeight}px`
        })
      }
    },
    handleFocus (event, optionIndex) {
      this.focusedIndex = optionIndex
      const textarea = event.target

      requestAnimationFrame(() => {
        // Calculate proper height
        textarea.style.height = '32px'
        const scrollHeight = Math.min(textarea.scrollHeight, 100)
        textarea.style.height = `${scrollHeight}px`
      })
    },
    handleBlur (event, optionIndex, subjectIndex) {
      const textarea = event.target

      requestAnimationFrame(() => {
        textarea.style.height = '32px'
      })

      // Check for empty value
      const value = this.question.subjects[subjectIndex].options[optionIndex].value
      if (!value?.trim()) {
        this.$set(this.question.subjects[subjectIndex].options[optionIndex], 'error', this.$t('survey.required_field'))
      }

      this.focusedIndex = null
    },
    handleTextareaClick (event, optionIndex, subjectIndex) {
      event.preventDefault()
      this.handleFocus(event, optionIndex, subjectIndex)
    },
    preventInvalidInput (event) {
      if (event.key === 'Enter') {
        event.preventDefault()
      }
    },
    handleLimitInput (event, subjectIndex, type) {
      const subject = this.question.subjects[subjectIndex]
      const currentOptionsCount = subject.options.length
      const input = event.target

      // Get input value and clean it to only allow numbers
      let value = input.value.replace(/[^0-9]/g, '')
      value = value ? parseInt(value) : ''

      // Don't proceed if empty (allow user to clear the input)
      if (value === '') {
        this.$set(this.limitErrors, subjectIndex, '')
        this.$set(this.hasMinErrors, subjectIndex, false)
        this.$set(this.hasMaxErrors, subjectIndex, false)
        return
      }

      // Immediately enforce limits
      if (value < 1) {
        value = 1
      }
      if (value > currentOptionsCount) {
        value = currentOptionsCount
        this.$set(this.limitErrors, subjectIndex, this.$t('survey.value_exceeds_options', { count: currentOptionsCount }))
        this.$set(type === 'min' ? this.hasMinErrors : this.hasMaxErrors, subjectIndex, true)
      } else {
        this.$set(this.limitErrors, subjectIndex, '')
        this.$set(this.hasMinErrors, subjectIndex, false)
        this.$set(this.hasMaxErrors, subjectIndex, false)
      }

      // Update the value immediately with the corrected value
      this.$set(subject, type, value)

      // Update the input value to reflect the corrected value
      input.value = value

      // Validate min/max relationship
      if (type === 'min' && subject.max && value > subject.max) {
        this.$set(subject, 'max', value)
      } else if (type === 'max' && subject.min && value < subject.min) {
        this.$set(subject, 'min', value)
      }

      // Clear any existing debounce timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }

      // Debounce the store update
      this.debounceTimer = setTimeout(() => {
        this.$store.commit('survey/upsertQuestion', { ...this.question })
        // Restore focus to the input after the update
        this.$nextTick(() => {
          input.focus()
        })
      }, 1500)
    },
    validateLimits (subjectIndex) {
      const subject = this.question.subjects[subjectIndex]

      // Ensure we have valid numbers
      if (!subject.min) subject.min = 1
      if (!subject.max) subject.max = subject.options.length

      // Final validation on blur
      if (subject.min < 1) subject.min = 1
      if (subject.max > subject.options.length) subject.max = subject.options.length
      if (subject.min > subject.max) subject.min = subject.max

      // Clear errors for this subject
      this.$set(this.limitErrors, subjectIndex, '')
      this.$set(this.hasMinErrors, subjectIndex, false)
      this.$set(this.hasMaxErrors, subjectIndex, false)

      this.$store.commit('survey/upsertQuestion', this.question)
    },
    validateLimitsWithOptions (subjectIndex) {
      const subject = this.question.subjects[subjectIndex]
      const currentOptionsCount = subject.options.length

      // Adjust max if it exceeds current options count
      if (subject.max && subject.max > currentOptionsCount) {
        this.$set(subject, 'max', currentOptionsCount)
      }

      // Ensure min doesn't exceed current options count
      if (subject.min && subject.min > currentOptionsCount) {
        this.$set(subject, 'min', currentOptionsCount)
      }

      // Ensure min doesn't exceed max
      if (subject.min && subject.max && subject.min > subject.max) {
        this.$set(subject, 'min', subject.max)
      }

      // Clear errors for this subject
      this.$set(this.limitErrors, subjectIndex, '')
      this.$set(this.hasMinErrors, subjectIndex, false)
      this.$set(this.hasMaxErrors, subjectIndex, false)

      this.$store.commit('survey/upsertQuestion', this.question)
    },
    canAddImage (subjectIndex) {
      return this.question.subjects[subjectIndex].options.length < 10
    },
    toggleAllTitleLabels () {
      this.$set(this.question, 'hideTitleLabels', !this.question.hideTitleLabels)
      this.$store.commit('survey/upsertQuestion', this.question)
    },
    toggleAllSubjectLabels () {
      this.$set(this.question, 'hideAllSubjectLabels', !this.question.hideAllSubjectLabels)
      this.$store.commit('survey/upsertQuestion', this.question)
    },
    checkMove (e) {
      // Prevent dragging if the element has the no-drag class
      return !e.draggedContext.element.value?.includes('add-button')
    }
  },
  created () {
    if (!this.question.subjects) {
      const subjects = [
        {
          label: this.$i18n.t('survey.option_1'),
          options: this.defaultImageOptions(),
          min: 1,
          max: 1
        }
      ]
      this.question.subjects = subjects
      this.question.options = []
      this.$store.commit('survey/upsertQuestion', this.question)
    }

    if (!this.question.imageOnTitle.length) {
      this.question.imageOnTitle = this.defaultImageOptions()
      this.$store.commit('survey/upsertQuestion', this.question)
    }

    // Initialize titleImageSize if not set
    if (!this.question.titleImageSize) {
      this.$set(this.question, 'titleImageSize', 'medium')
      this.$store.commit('survey/upsertQuestion', this.question)
    }

    // Initialize optionImageSize if not set
    if (!this.question.optionImageSize) {
      this.$set(this.question, 'optionImageSize', 'medium')
      this.$store.commit('survey/upsertQuestion', this.question)
    }

    // Initialize hideTitleLabels if not set
    if (typeof this.question.hideTitleLabels === 'undefined') {
      this.$set(this.question, 'hideTitleLabels', false)
      this.$store.commit('survey/upsertQuestion', this.question)
    }

    // Initialize hideAllSubjectLabels if not set
    if (typeof this.question.hideAllSubjectLabels === 'undefined') {
      this.$set(this.question, 'hideAllSubjectLabels', false)
      this.$store.commit('survey/upsertQuestion', this.question)
    }
    // Initialize titleImageSize if not set
    if (!this.question.titleImageSize) {
      this.$set(this.question, 'titleImageSize', 'medium')
      this.$store.commit('survey/upsertQuestion', this.question)
    }

    // Initialize showImageOnTitle if not set
    if (typeof this.question.showImageOnTitle === 'undefined') {
      this.$set(this.question, 'showImageOnTitle', true)
      this.$store.commit('survey/upsertQuestion', this.question)
    }
  },
  beforeDestroy () {
    // Clear the timer when component is destroyed
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  },
  watch: {
    'question.subjects': {
      deep: true,
      handler (_newVal, _oldVal) {
        // Only update if the values actually changed
        if (JSON.stringify(_newVal) !== JSON.stringify(_oldVal)) {
          this.$store.commit('survey/upsertQuestion', this.question)
        }
      }
    },
    currentTitleSize (_newVal) {
      this.$store.commit('survey/upsertQuestion', this.question)
    },
    currentOptionSize (_newVal) {
      this.$store.commit('survey/upsertQuestion', this.question)
    }
  },
  mounted () {
    // Initialize heights for all textareas
    this.$nextTick(() => {
      document.querySelectorAll('.option-value-input').forEach(textarea => {
        textarea.style.height = 'auto'
        const minHeight = 32
        const contentHeight = Math.max(textarea.scrollHeight, minHeight)
        textarea.style.height = `${contentHeight}px`
      })
    })
  }
}
</script>
<style lang="scss">
.content-image-choice {
  padding: 20px;
  position: relative;
  z-index: 1;
  width: 100%;
  // Common section styles
  .section-block {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    margin-bottom: 24px;
    overflow: visible;
    position: relative;

    .section-header {
      background: #f8f8f8;
      border-bottom: 1px solid #eee;
      padding: 20px;
      min-height: 80px;
      align-content: center;
      position: relative;
      z-index: 2;

      .section-title {
        margin: 0 auto;
        position: relative;
        display: flex;
        flex-flow: column;

        .view-mode {
          margin-left: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          gap: 30px;
          .control-group {
            display: flex;
            flex-direction: column;
            gap: 6px;

            .control-label {
              font-size: 12px;
              color: #666;
              font-weight: 500;
            }

            .size-options {
              position: relative;
              display: inline-block;

              .size-toggle {
                display: flex;
                align-items: center;
                padding: 8px 12px;
                border: 1px solid #eee;
                border-radius: 6px;
                background: white;
                color: #666;
                font-size: 14px;
                cursor: pointer;
                min-width: 160px;
                transition: all 0.2s ease;

                i {
                  &:first-child {
                    margin-right: 8px;
                    font-size: 20px;
                  }

                  &:last-child {
                    margin-left: auto;
                    font-size: 20px;
                  }
                }

                &:hover {
                  border-color: #ddd;
                  background: #f8f8f8;
                }
              }

              .size-menu {
                position: absolute;
                right: 0;
                top: calc(100% + 4px);
                background: white;
                border: 1px solid #eee;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                overflow: hidden;
                z-index: 9999;
                min-width: 180px;

                .size-option {
                  display: flex;
                  align-items: center;
                  width: 100%;
                  padding: 8px 12px;
                  border: none;
                  background: none;
                  cursor: pointer;
                  color: #666;
                  font-size: 14px;
                  text-align: left;
                  transition: all 0.2s ease;

                  i {
                    margin-right: 8px;
                    font-size: 20px;
                  }

                  &:hover {
                    background: #f5f5f5;
                  }

                  &.active {
                    background: #f0f0f0;
                    color: var(--accent-color, #ff607d);
                  }
                }
              }
            }

            .label-toggle {
              .toggle-switch {
                position: relative;
                display: inline-flex;
                align-items: center;
                gap: 8px;
                cursor: pointer;

                input {
                  opacity: 0;
                  width: 0;
                  height: 0;

                  &:checked + .toggle-slider {
                    background-color: var(--accent-color, #ff607d);
                  }

                  &:checked + .toggle-slider:before {
                    transform: translateX(20px);
                  }
                }

                .toggle-slider {
                  position: relative;
                  display: inline-block;
                  width: 44px;
                  height: 24px;
                  background-color: #ccc;
                  transition: .3s;
                  border-radius: 24px;
                  flex-shrink: 0;

                  &:before {
                    position: absolute;
                    content: "";
                    height: 18px;
                    width: 18px;
                    left: 3px;
                    bottom: 3px;
                    background-color: white;
                    transition: .3s;
                    border-radius: 50%;
                  }
                }

                .toggle-label {
                  font-size: 14px;
                  color: #666;
                  min-width: 48px;
                }
              }
            }
          }
        }
      }
    }

    .section-content {
      padding: 20px;
    }
  }

  // Specific styles for title section
  .title-section {
    .section-content {
      padding: 20px;
    }
  }

  // Specific styles for subjects section
  .subjects-section {
    .section-content {
      .subject-container {
        border: 1px solid #eee;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 16px;
        transition: all 0.2s ease;

        &:hover {
          border-color: var(--accent-color, #ff607d);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .subject-limits {
          display: flex;
          flex-direction: column;
          gap: 8px;
          width: 100%;
          padding: 16px;
          background: #fff;
          border-radius: 8px;
          border: 1px solid #eee;
          position: relative;
          padding-bottom: 32px;
          margin: 0 auto;

          .limit-group {
            display: flex;
            gap: 24px;
            justify-content: center;
          }

          .limit-input {
            display: flex;
            flex-direction: column;
            gap: 6px;
            width: 120px;

            label {
              font-size: 12px;
              color: #666;
              font-weight: 500;
            }

            .limit-value-input {
              width: 100%;
              height: 36px;
              padding: 4px 12px;
              border: 1px solid #ddd;
              border-radius: 4px;
              font-size: 14px;
              text-align: center;
              transition: all 0.2s ease;
              -moz-appearance: textfield;
              background: #fff;

              &::-webkit-outer-spin-button,
              &::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
              }

              &:focus {
                outline: none;
                border-color: var(--accent-color, #ff607d);

              }

              &:hover {
                border-color: #bbb;
              }

              &.invalid {
                border-color: #ff607d;
                background-color: rgba(255, 96, 125, 0.05);
              }
            }
          }
        }

        .limit-hint {
          text-align: center;
          font-size: 12px;
          color: #666;
          padding-top: 4px;
        }

        .limit-error {
          position: absolute;
          bottom: 8px;
          left: 0;
          right: 0;
          text-align: center;
          font-size: 12px;
          color: #ff607d;
          min-height: 20px;
        }
      }
    }
  }

  // Remove old block styles
  .title-images-block,
  .subjects-block,
  .global-controls {
    display: none;
  }

  // Preview mode specific styles
  &.preview-mode {
    padding: 0;
    margin: 0;
    height: auto;
    transform: none;

    .preview-container {
      padding: 6px;

      .preview-samples {
        background: #f8f8f8;
        border-radius: 8px;
        padding: 8px;
      }

      .preview-title-images,
      .preview-subject-options {
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      .preview-divider {
        height: 1px;
        background: #eee;
        margin: 6px 0;
        width: 100%;
      }

      .preview-thumbs {
        display: flex;
        align-items: center;
        gap: 4px;
        margin-bottom: 2px;

        .preview-thumb-box {
          width: 20px;
          height: 20px;
          background-color: #f0f0f0;
          border: 1px solid #ddd;
          border-radius: 4px;
        }

        .preview-more {
          background-color: var(--accent-color, #ff607d);
          color: white;
          font-size: 10px;
          padding: 2px 4px;
          border-radius: 4px;
          min-width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      .preview-count {
        font-size: 10px;
        color: #666;
      }
    }
  }

  // Special styles for preview mode
  &.preview-mode {
    padding-top: 0;
    transform: none;

    .subject-container {
      border: none;
      padding: 0;
      margin-bottom: 5px;
    }
    .content-subject {
      width: auto;
    }
  }

  .content-subject {
    margin: 1vw;
  }

  .subject-container {
    margin-bottom: 20px;
    padding: 20px;
    border: 2px dashed #e0e0e0;
    border-radius: 8px;
    transition: border-color 0.2s ease;
    position: relative;

    &:hover {
      border-color: var(--accent-color, #ff607d);
    }

    // Add header section for the subject container
    .subject-header {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-bottom: 20px;
      width: 100%;
      margin: 0 auto 20px;

      .subject-label {
        width: 100%;
        margin: 0 auto;

        .description-area {
          width: 100%;
          margin: 0 !important;
          :deep(textarea) {
            width: 100%;
            min-height: 36px;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            resize: none;
            text-align: center;
            background: #fff;

            &:focus {
              outline: none;
              border-color: var(--accent-color, #ff607d);

            }
          }
          textarea {
              text-align: center;
              border-style: dashed;

              &:focus {
                outline: none;
                border-color: var(--accent-color, #ff607d);

              }
            }
        }
      }
    }

    // Update the clean delete button positioning
    .clean-delete-button {
      position: absolute;
      top: -4px;
      right: -4px;
      background: none;
      border: none;
      padding: 8px;
      cursor: pointer;
      opacity: 0.7;
      transition: opacity 0.2s ease, transform 0.2s ease;
      z-index: 2;

      i {
        font-size: 20px;
      }

      &:hover {
        opacity: 1;
        transform: scale(1.2);
      }
    }
  }

  .subject-options-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 15px;
    width: 100%;
  }

  .subject-options {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
  }

  .draggable-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 15px;
    width: 100%;
    min-height: 150px;
    padding: 15px;
    box-sizing: border-box;
    pointer-events: auto !important;
  }

  .subject-image-choice {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    cursor: move;
    cursor: grab;
    margin-bottom: 15px;
    transition: all 0.3s ease;

    .delete-button {
        position: absolute;
        top: -8px;
        right: -8px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: white;
        border: none;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        cursor: pointer;
        z-index: 3;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        opacity: 0;
        transform: scale(0.8);

        i {
          font-size: 18px;
          color: var(--accent-color, #ff607d);
        }

        &:hover {
          transform: scale(1.1);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
        }
      }

      &:hover .delete-button {
        opacity: 1;
        transform: scale(1);
      }

    // Dynamic sizes based on selectedOptionSize
    &[data-size="small"] {
      width: calc(25% - 15px);
      min-width: 104px;
      .option-wrapper, .add-wrapper {
        width: 100px !important;
        height: 100px !important;
      }
      .image-uploader-container {
        width: 100px;
        height: 100px;
      }
    }

    &[data-size="medium"] {
      width: calc(33.33% - 15px);
      min-width: 150px;

      .image-container {
        position: relative;
        width: 100%;
        display: flex;
        justify-content: center;
        margin-bottom: 8px;
      }

      .image-wrapper {
        position: relative;
        width: 160px;
        height: 160px;
        background-color: #f8f8f8;
        border-radius: 4px;
        overflow: visible;
        transition: none;

        :deep(.image-uploader-container),
        :deep(.content-upload-setImage),
        :deep(.uploader-content),
        :deep(.upload-wrapper) {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }
      }
    }

    &[data-size="large"] {
      width: calc(38% - 15px);
      min-width: 400px;

      .image-container {
        position: relative;
        width: 100%;
        display: flex;
        justify-content: center;
        margin-bottom: 8px;
      }

      .image-wrapper {
        position: relative;
        width: 200px;
        height: 200px;
        background-color: #f8f8f8;
        border-radius: 4px;
        overflow: visible;
        transition: none;

        :deep(.image-uploader-container),
        :deep(.content-upload-setImage),
        :deep(.uploader-content),
        :deep(.upload-wrapper) {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }
      }
    }

    @media (max-width: 768px) {
      &[data-size="small"] {
        width: calc(33.33% - 15px);

        .image-container {
          width: 140px;
          height: 140px;
        }
      }

      &[data-size="medium"] {
        width: calc(50% - 15px);

        .image-container {
          width: 180px;
          height: 180px;
        }
      }

      &[data-size="large"] {
        width: 100%;

        .image-container {
          width: 220px;
          height: 220px;
        }
      }
    }

    @media (max-width: 480px) {
      width: 100% !important;

      .image-container {
        width: 180px !important;
        height: 180px !important;
      }
    }

    &:active {
      cursor: grabbing;
    }

    // Update the spacing when value is hidden
    .image-wrapper {
      margin-bottom: 0; // Remove bottom margin when value is hidden
    }

    .option-value {
      margin-top: 8px;
      max-width: 200px;
      text-align: center;
      padding: 0px;
      box-sizing: border-box;

      .input-wrapper {
        position: relative;
        width: 100%;

        textarea.option-value-input {
          width: 100%;
          height: 32px;
          min-height: 32px;
          margin: 0;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 13px;
          color: #333;
          text-align: center;
          background-color: #fff;
          box-sizing: border-box;
          resize: none;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 1.4;
          will-change: transform, height;
          transform-origin: top;

          // Improved transition properties
          transition:
            height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
            border-color 0.2s ease,
            background-color 0.2s ease;

          &.is-focused {
            max-height: 100px;
            text-align: left;
            white-space: pre-wrap;
            overflow-y: auto;
            background-color: #fff;
            border-color: var(--accent-color, #ff607d);
            box-shadow: 0 0 0 2px rgba(255, 96, 125, 0.1);
          }

          &:not(.is-focused) {
            background-color: #f8f8f8;
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            user-select: none;
            cursor: pointer;
          }

          &.has-error {
            border-color: var(--accent-color, #ff607d);
            background-color: #fff9f9;

            &::placeholder {
              color: var(--accent-color, #ff607d);
            }
          }
        }

        .character-counter {
          position: absolute;
          right: 0;
          bottom: -17px;
          font-size: 11px;
          color: #666;
          text-align: right;
          background: white;
          padding: 2px 4px;
          border-radius: 2px;
        }
      }
    }

    &.add-image-button {
      cursor: pointer !important;

      &.disabled {
        cursor: not-allowed !important;
        opacity: 0.5;

        .add-wrapper {
          border-color: #ddd !important;
          background: #f5f5f5 !important;

          .add-content {
            color: #999 !important;
          }
        }

        &:hover .add-wrapper {
          border-color: #ddd !important;
          background: #f5f5f5 !important;

          .add-content {
            color: #999 !important;
          }
        }
      }

      .add-wrapper {
        border: 2px dashed #ddd;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        background: white;
        width: 160px;
        height: 160px;

        .add-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          color: #666;

          i {
            font-size: 32px;
          }

          span {
            font-size: 14px;
          }
        }
      }

      &:hover .add-wrapper {
        border-color: var(--accent-color, #ff607d);
        background: #fff;

        .add-content {
          color: var(--accent-color, #ff607d);
        }
      }
    }
  }

  .option-wrapper {
    position: relative;
    margin-bottom: 8px;
    background-color: #f8f8f8;
    border-radius: 4px;
    overflow: visible;
    transition: none;

    :deep(.image-uploader-container),
    :deep(.content-upload-setImage),
    :deep(.uploader-content),
    :deep(.upload-wrapper) {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }

  .option-value {
    margin-top: 8px;
    width: 100%;
    text-align: center;
    padding: 0px;
    box-sizing: border-box;
  }

  .option-error {
    color: var(--accent-color, #ff607d);
    font-size: 11px;
    margin-top: 4px;
    text-align: left;
  }

  .add-item {
    margin-top: 20px;
    text-align: center;

    button {
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        opacity: 0.9;
        transform: scale(1.1);
      }
    }
  }

  .delete-subject {
    margin-top: 10px;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .delete-subject-button {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    border: none;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.3s ease;

    i {
      margin-right: 5px;
    }

    &:hover {
      opacity: 0.9;
      transform: scale(1.03);
    }
  }

  .drag-handle {
    position: absolute;
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0.5;
    transition: opacity 0.3s ease;
    cursor: grab;
    z-index: 10;
    background-color: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    padding: 2px;

    &:active {
      cursor: grabbing;
    }

    i {
      font-size: 20px;
      display: block;
    }
  }

  .subject-image-choice:hover .drag-handle {
    opacity: 1;
  }

  // Additional styles for dragging state
  .dragging {
    .option-wrapper {
      opacity: 0.7;
    }

    .menu-container {
      display: none !important;
    }
  }

  // Updated drag styling
  .ghost-card {
    opacity: 0.5;
    background: #f0f0f0;
    border: 1px dashed #999;

    .menu-container {
      display: none !important;
    }
  }

  .dragging {
    opacity: 0.8;
    transform: rotate(2deg);

    .menu-container {
      display: none !important;
    }
  }

  .drag-handle-container {
    width: 100%;
    display: flex;
    justify-content: center;
    position: absolute;
    top: -15px;

    .drag-handle {
      font-size: 20px;
      color: #777;
      background-color: #fff;
      border-radius: 50%;
      cursor: grab;
      padding: 2px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.12);

      &:hover {
        color: var(--accent-color, #ff607d);
      }
    }
  }

  // New styles for preview mode
  .preview-subject {
    border: none !important;
    padding: 5px !important;
    margin-bottom: 0 !important;

    .subject-label {
      h6 {
        font-size: 14px !important;
        margin: 0 0 5px 0 !important;
        text-align: center;
      }
    }
  }

  .preview-options-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    width: 100%;
  }

  .preview-image-choice {
    width: auto;
    max-width: 80px;
    margin: 0 2px 5px;
    text-align: center;
  }

  .preview-image-wrapper {
    width: 55px;
    height: 55px;
    overflow: hidden;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f2f2f2;
    border-radius: 4px;
  }

  .preview-option-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .preview-no-image {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #999;

    i {
      font-size: 24px;
    }
  }

  .preview-option-value {
    font-size: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    margin-top: 2px;
  }

  // Size selector styles
  .size-selector {
    margin-bottom: 15px;
    display: flex;
    justify-content: center;

    .size-options {
      position: relative;
      display: inline-block;

      select {
        appearance: none;
        -webkit-appearance: none;
        background: #fff;
        border: 1px solid #eee;
        border-radius: 6px;
        padding: 8px 12px;
        padding-right: 32px;
        cursor: pointer;
        min-width: 160px;
        font-size: 14px;
        color: #666;

        &:focus {
          outline: none;
          border-color: var(--accent-color, #ff607d);

        }
      }

      &::after {
        content: '';
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 5px solid #666;
        pointer-events: none;
      }

      .material-symbols-outlined {
        margin-right: 8px;
        vertical-align: middle;
      }
    }
  }

  .option-list-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 15px;
    width: 100%;
  }

  // Add transition animations
  .option-list-enter-active {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .option-list-leave-active {
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
    position: absolute;
  }

  .option-list-enter-from {
    opacity: 0;
    transform: translateY(8px);
  }

  .option-list-leave-to {
    opacity: 0;
    transform: scale(0.95);
  }

  .option-list-move {
    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  // Update subject-image-choice to include transition
  .subject-image-choice {
    transition: all 0.3s ease;
    // ... existing styles ...
  }

  // Also add transition for subject containers
  .subject-container {
    transition: all 0.3s ease;
    // ... existing styles ...
  }

  .no-drag {
    cursor: pointer !important;
    user-select: none !important;
    -webkit-user-drag: none !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
  }

  .draggable-container .no-drag {
    pointer-events: auto !important;
  }
}

.title-with-toggle {
  display: flex;
  align-items: center;
  gap: 12px;

  .toggle-switch {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;

    input {
      opacity: 0;
      width: 0;
      height: 0;

      &:checked + .toggle-slider {
        background-color: var(--accent-color, #ff607d);
      }

      &:checked + .toggle-slider:before {
        transform: translateX(20px);
      }
    }

    .toggle-slider {
      position: absolute;
      cursor: pointer;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: #ccc;
      transition: .3s;
      border-radius: 24px;

      &:before {
        position: absolute;
        content: "";
        height: 18px;
        width: 18px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        transition: .3s;
        border-radius: 50%;
      }
    }
  }
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 16px;
  gap: 24px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: #333;
    white-space: nowrap;
  }

  .view-mode {
    margin-left: auto;
    .size-options {
      position: relative;
      display: inline-block;

      .size-toggle {
        display: flex;
        align-items: center;
        padding: 8px 12px;
        border: 1px solid #eee;
        border-radius: 6px;
        background: white;
        color: #666;
        font-size: 14px;
        cursor: pointer;
        min-width: 160px;
        transition: all 0.2s ease;

        i {
          &:first-child {
            margin-right: 8px;
            font-size: 20px;
          }

          &:last-child {
            margin-left: auto;
            font-size: 20px;
          }
        }

        &:hover {
          border-color: #ddd;
          background: #f8f8f8;
        }
      }

      .size-menu {
        position: absolute;
        right: 0;
        top: calc(100% + 4px);
        background: white;
        border: 1px solid #eee;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        overflow: hidden;
        z-index: 9999;
        min-width: 180px;

        .size-option {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 8px 12px;
          border: none;
          background: none;
          cursor: pointer;
          color: #666;
          font-size: 14px;
          text-align: left;
          transition: all 0.2s ease;

          i {
            margin-right: 8px;
            font-size: 20px;
          }

          &:hover {
            background: #f5f5f5;
          }

          &.active {
            background: #f0f0f0;
            color: var(--accent-color, #ff607d);
          }
        }
      }
    }
  }
}

.subject-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
  width: 100%;
  margin: 0 auto 20px;

  .subject-label {
    width: 100%;
    margin: 0 auto;

    .description-area {
      width: 100%;

      :deep(textarea) {
        width: 100%;
        min-height: 36px;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        resize: none;
        text-align: center;
        background: #fff;

        &:focus {
          outline: none;
          border-color: var(--accent-color, #ff607d);
        }
      }
    }
  }
}

.subject-limits {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #eee;
  position: relative;
  padding-bottom: 32px;
  margin: 0 auto;

  .limit-group {
    display: flex;
    gap: 24px;
    justify-content: center;
  }

  .limit-input {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 120px;

    label {
      font-size: 12px;
      color: #666;
      font-weight: 500;
    }

    .limit-value-input {
      width: 100%;
      height: 36px;
      padding: 4px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
      text-align: center;
      transition: all 0.2s ease;
      -moz-appearance: textfield;
      background: #fff;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &:focus {
        outline: none;
        border-color: var(--accent-color, #ff607d);

      }

      &:hover {
        border-color: #bbb;
      }

      &.invalid {
        border-color: #ff607d;
        background-color: rgba(255, 96, 125, 0.05);
      }
    }
  }
}

.limit-hint {
  text-align: center;
  font-size: 12px;
  color: #666;
  padding-top: 4px;
}

.limit-error {
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 12px;
  color: #ff607d;
  min-height: 20px;
}

// Add global styles for tooltips at the end of the file
:deep([data-v-tooltip]) {
  z-index: 100000 !important;
}

:deep(.v-tooltip) {
  z-index: 100000 !important;
}

// Add visual feedback for character limit
.option-value-input {
  &:invalid {
    border-color: var(--accent-color, #ff607d);
    background-color: rgba(255, 96, 125, 0.05);
  }
}

// Optional: Add a character counter
.option-value {
  position: relative;

  .input-wrapper {
    position: relative;
    width: 100%;

    // Remove or override the ::after pseudo-element
    &::after {
      display: none; // This will hide the arrow
    }
  }

  .character-counter {
    position: absolute;
    right: 8px;
    bottom: -20px;
    font-size: 11px;
    color: #666;
    text-align: right;
  }

  input {
    // ... existing input styles ...
    padding-right: 70px; // Make room for counter
  }
}
</style>
